import { useState, useEffect, useRef, useCallback } from 'react';
import './Gallery.css';

const IMAGES = [
  { src: '/gallery1.jpg', alt: 'Kevin Adams in the community' },
  { src: '/gallery2.jpg', alt: 'Kevin Adams in the community' },
  { src: '/gallery3.jpg', alt: 'Kevin Adams in the community' },
  { src: '/gallery4.jpg', alt: 'Kevin Adams in the community' },
  { src: '/gallery5.jpg', alt: 'Kevin Adams in the community' },
  { src: '/gallery6.jpg', alt: 'Kevin Adams in the community' },
  { src: '/gallery7.jpg', alt: 'Kevin Adams in the community' },
];

function Gallery() {
  const pageRef = useRef();
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = pageRef.current?.querySelectorAll('.animate');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const close = useCallback(() => setActiveIndex(null), []);
  const prev = useCallback(
    () => setActiveIndex((i) => (i === 0 ? IMAGES.length - 1 : i - 1)),
    []
  );
  const next = useCallback(
    () => setActiveIndex((i) => (i === IMAGES.length - 1 ? 0 : i + 1)),
    []
  );

  useEffect(() => {
    if (activeIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [activeIndex, close, prev, next]);

  return (
    <div className="gallery-page" ref={pageRef}>
      {/* HERO */}
      <section className="gallery-hero">
        <div className="gallery-hero__inner">
          <span className="label animate">Gallery</span>
          <h1 className="animate">Kevin in the community</h1>
          <p className="animate">
            Moments from across Horseheads — connecting with neighbors,
            supporting local events, and serving the village we call home.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="gallery-grid-section">
        <div className="gallery-grid">
          {IMAGES.map((img, i) => (
            <button
              key={img.src}
              type="button"
              className={`gallery-tile animate gallery-tile--${(i % 5) + 1}`}
              onClick={() => setActiveIndex(i)}
              aria-label={`Open image ${i + 1}`}
            >
              <img src={img.src} alt={img.alt} loading="lazy" />
              <span className="gallery-tile__overlay">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              </span>
            </button>
          ))}
        </div>

        <div className="gallery-more animate">
          <p>Currently serving in office — and ready to run again in 2027</p>
          <a
            href="https://www.facebook.com/KevinAdamsNY/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn--primary"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
            </svg>
            Follow on Facebook
          </a>
        </div>
      </section>

      {/* LIGHTBOX */}
      {activeIndex !== null && (
        <div className="lightbox" onClick={close} role="dialog" aria-modal="true">
          <button
            type="button"
            className="lightbox__close"
            onClick={close}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <button
            type="button"
            className="lightbox__nav lightbox__nav--prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
            <img src={IMAGES[activeIndex].src} alt={IMAGES[activeIndex].alt} />
            <span className="lightbox__counter">
              {activeIndex + 1} / {IMAGES.length}
            </span>
          </div>

          <button
            type="button"
            className="lightbox__nav lightbox__nav--next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default Gallery;
